/**
 * AMD-53 — the Salvage Spin. A boss carcass is worth a gamble.
 *
 * Borrowed from kimi-test-matt's between-waves bonus game, reshaped to this spine: no new
 * modal, no new currency, no held state. On every `boss_defeated` a three-reel spin
 * resolves instantly — triple pays a jackpot, pair pays a find, miss pays scraps — and
 * the payout flows through the AMD-35 ichor pipeline, so the HUD streak, the counter and
 * the Graft affordability cue all light up for free.
 *
 * Determinism is the kimi salt pattern (their shop's UpgradeSystem-salt): each spin draws
 * from a one-shot stream seeded `runSeed ^ SALT ^ bossIndex`. No bank stream is touched,
 * no draw count leaks into any other system, and the Nth boss of a seed always spins the
 * same reels — replayable, probeable, and immune to event-order drift.
 */

import { RngStream } from '../core/rng';
import { grantIchor } from './gacha';
import type { World } from './world';

const SALVAGE_SALT = 0x5a17a6e5;

/** Reel faces. Three of a kind pays `jackpot`, a pair pays `pair`, a miss pays `scraps`. */
const FACES = 5;
export const PAYOUT = { jackpot: 60, pair: 25, scraps: 8 } as const;

export interface SalvageResult {
  reels: [number, number, number];
  tier: keyof typeof PAYOUT;
  ichor: number;
}

/** Pure: the Nth boss of this seed always spins these reels. */
export function spinFor(runSeed: number, bossIndex: number): SalvageResult {
  const rng = new RngStream((runSeed ^ SALVAGE_SALT ^ Math.imul(bossIndex + 1, 0x9e3779b1)) >>> 0);
  const reels: [number, number, number] = [
    Math.floor(rng.next() * FACES),
    Math.floor(rng.next() * FACES),
    Math.floor(rng.next() * FACES),
  ];
  const tier: keyof typeof PAYOUT =
    reels[0] === reels[1] && reels[1] === reels[2]
      ? 'jackpot'
      : reels[0] === reels[1] || reels[1] === reels[2] || reels[0] === reels[2]
        ? 'pair'
        : 'scraps';
  return { reels, tier, ichor: PAYOUT[tier] };
}

interface SalvageState {
  installed: boolean;
  bossesDown: number;
}

const STATES = new WeakMap<World, SalvageState>();

function stateOf(w: World): SalvageState {
  let st = STATES.get(w);
  if (st === undefined) {
    st = { installed: false, bossesDown: 0 };
    STATES.set(w, st);
  }
  return st;
}

/** Subscribes the spin to `boss_defeated`. Idempotent, like `installGacha`. */
export function installSalvage(w: World): void {
  const st = stateOf(w);
  if (st.installed) return;
  st.installed = true;
  w.bus.on('boss_defeated', () => {
    const spin = spinFor(w.runSeed, st.bossesDown++);
    grantIchor(w, spin.ichor);
    w.bus.emit('salvage', { reels: spin.reels, tier: spin.tier, ichor: spin.ichor });
  });
}
